import { useState } from "react";
import { motion } from "framer-motion";
import {
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api from "../Component/Api";
import "../Styles/Contact.css";

const Contact = () => {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/contact-us", form);

      toast.success(response.data.msg);

      setForm({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.msg || "Something went wrong."
      );
    } finally {
      setLoading(false);
    }
  };

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 35,
    },

    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="contact-us" className="contact-page">
      <div className="contact-container">

        {/* Header */}
        <motion.div
          className="contact-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p className="contact-eyebrow">
            CONTACT
          </p>

          <h2 className="contact-title">
            Let's build your
            <br />
            <span>
              digital business card.
            </span>
          </h2>

          <p className="contact-subtitle">
            Whether you're ordering your first wristband,
            collaborating with ClusterClear or have a question,
            we'd love to hear from you.
          </p>
        </motion.div>

        <div className="contact-grid">

          {/* LEFT SIDE */}
          <motion.div
            className="contact-left"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >

            {/* Email */}
            <div className="contact-card">
              <div className="contact-icon">
                <HiMail size={22} />
              </div>

              <div className="contact-card-content">
                <p className="contact-label">
                  Email
                </p>

                <a
                  href="mailto:support@clusterclear.com"
                  className="contact-link"
                >
                  support@clusterclear.com
                </a>
              </div>
            </div>

            {/* Instagram */}
            <div className="contact-card">
              <div className="contact-icon">
                <FaInstagram size={20} />
              </div>

              <div className="contact-card-content">
                <p className="contact-label">
                  Instagram
                </p>

                <a
                  href="https://instagram.com/clusterclear_01"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  @clusterclear_01
                </a>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="contact-card">
              <div className="contact-icon">
                <FaWhatsapp size={20} />
              </div>

              <div className="contact-card-content">
                <p className="contact-label">
                  WhatsApp
                </p>

                <a
                  href="https://wa.me/447000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  Chat with ClusterClear
                </a>
              </div>
            </div>

          </motion.div>

          {/* RIGHT SIDE */}
          <motion.form
            onSubmit={handleSubmit}
            className="contact-form"
            initial={{
              opacity: 0,
              y: 40,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
          >

            {/* Name */}
            <div className="contact-field">
              <label>
                Full name
              </label>

              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="First name and last name"
                required
              />
            </div>

            {/* Email */}
            <div className="contact-field">
              <label>
                Email address
              </label>

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>

            {/* Message */}
            <div className="contact-field">
              <label>
                Message
              </label>

              <textarea
                rows={6}
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us how we can help..."
                required
              />
            </div>

            {/* Button */}
            <motion.button
              type="submit"
              whileHover={{
                scale: 1.02,
              }}
              whileTap={{
                scale: 0.98,
              }}
              disabled={loading}
              className="contact-button"
            >
              {loading
                ? "Sending..."
                : "Send message"}

              <ArrowUpRight size={18} />
            </motion.button>

          </motion.form>

        </div>
      </div>
    </section>
  );
};

export default Contact;